<template>
    <Teleport to="#payyo-payment-method-vue-app">
        <div id="payyo-payment-method">
            <ErrorMessage v-if="error"
                          :error="error"
                          class="mt-1">
                <!-- It would be quite fun to create component with a built timer -->
                <button class="btn btn-default mt-1"
                        :disabled="!enableErrorContinueButton"
                        @click="postErrorHandler()">
                    {{ l('PAYYO.ERROR_CONTINUE_BTN') }}
                </button>
            </ErrorMessage>
            <template v-else>
                <div v-if="ready"
                     class="payment-terminal-screen">
                    <div v-if="loading"
                         class="text-center loading-spinner dark">
                        <i class="fa-solid fa-spinner fa-spin"></i>
                        <p>{{ l('PAYMENT.WAITING_FOR_TERMINAL_READY') }}</p>
                    </div>
                    <div v-else>
                        <div v-if="currentTransaction"
                             data-testid="current-transaction-card"
                             class="message-line">
                            <span v-if="currentTransaction.amountInMajorUnits">
                                {{ l('PAYMENT.TXN_AMOUNT') }} {{ currencyFilter(currentTransaction.amountInMajorUnits) }}
                            </span>

                            <span v-if="currentTransaction.tipAmountInMajorUnits">
                                + {{ currencyFilter(currentTransaction.tipAmountInMajorUnits) }} {{ l('PAYMENT.GRATUITY') }}
                            </span>
                        </div>

                        <!-- A friendly version of the current state of the transaction
                             e.g. Present Card or Transaction Started -->
                        <div v-if="friendlyStatusMessage"
                             data-testid="friendly-status-message-card"
                             class="message-line">
                            <p>
                                {{ l(friendlyStatusMessage) }} <i v-if="isTransactionInProgress()"
                                                                  class="fa-solid fa-spinner fa-spin"></i>
                            </p>
                        </div>

                        <!-- Initially hidden. The end state of the transaction
                             Either: SUCCESS, CANCELLED, FAILED -->
                        <div v-if="currentTransaction?.transactionResult.message"
                             data-testid="transaction-result-card"
                             class="message-line"
                             :class="currentTransaction.transactionResult.class">
                            <p><b>{{ l(currentTransaction.transactionResult.message) }}</b></p>
                        </div>

                        <!-- Initially hidden. A message containing additional information supplied by Payyo that
                             we just display and do not process in any way.
                             e.g. card declined because... -->
                        <div v-if="currentTransaction?.userMessage"
                             data-testid="user-message-card"
                             class="message-line message-line-info">
                            <p>{{ currentTransaction.userMessage }}</p>
                        </div>

                        <div v-if="!isTransactionInProgress() && currentStartRestartButtonState === StartRestartButtonState.START">
                            <button data-testid="payyo-start-button"
                                    class="btn btn-block btn-success btn-lg"
                                    @click="initiateTransaction()">
                                {{ l('PAYMENT.START_CARD_TRANSACTION_BTN', {amount: getRemainingBalance()}) }}
                            </button>
                        </div>

                        <div v-if="showRefundablePaymentsButton">
                            <button data-testid="payyo-refundable-payments-button"
                                    class="btn btn-block btn-success"
                                    @click="showRefundablePaymentsModal()">
                                {{ l('PAYYO.REFUNDABLE_PAYMENTS_BTN') }}
                            </button>
                        </div>

                        <div v-if="showCancelButton()">
                            <button data-testid="payyo-cancel-button"
                                    class="btn btn-block btn-danger"
                                    :disabled="isCancelButtonDisabled()"
                                    @click="cancelPayment">
                                {{ l('PAYMENT.CANCEL_TRANSACTION_BTN_PAYYO') }}
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="currentTransaction?.paymentId && isTransactionInProgress()"
                     class="btn-row btn-row--no-stretch">
                    <SquareBtn data-testid="payyo-recheck-payment-status-button"
                               data-analytics-id="Payyo / Re-Check Status"
                               :data-ref="currentTransaction.thirdPartyID"
                               :loading="recheckingPaymentStatus"
                               @click="recheckPaymentStatus()">
                        <i class="fa-solid fa-refresh" />
                        {{ l('PAYMENT.RECHECK_BTN') }}
                    </SquareBtn>
                </div>

                <div v-if="ready && !loading"
                     class="btn-row btn-row--no-stretch">
                    <button data-testid="payyo-moto-payment-button"
                            class="btn btn-default"
                            :disabled="isMotoPaymentButtonDisabled()"
                            @click="makeMotoPayment()">
                        <i class="fa-solid fa-phone"></i>
                        {{ l('PAYMENT.CNP_TEL_BTN') }}
                    </button>

                    <button data-testid="payyo-partial-payment-button"
                            class="btn btn-default"
                            :disabled="isPartialPaymentButtonDisabled()"
                            @click="makePartialPayment()">
                        <i class="fa-solid fa-hexagon-divide"></i>
                        {{ l('PAYMENT.PARTIAL_PAYMENT_BTN_PAYYO') }}
                    </button>

                    <button data-testid="payyo-manual-reconciliation-button"
                            class="btn btn-default"
                            :disabled="isManualReconciliationButtonDisabled()"
                            @click="startManualReconciliation()">
                        <i class="fa-solid fa-clipboard-check"></i>
                        {{ l('PAYMENT.MANUAL_RECONCILIATION_BTN') }}
                    </button>
                </div>

                <div class="text-center dim mt-2">
                    <PayyoWebSocketStatus />
                </div>

                <div v-if="(isDev || isStaging) && currentTransaction?.paymentId">
                    <p class="text-center text-muted small">
                        Payment ID:
                        <span class="user-selectable">{{ currentTransaction.paymentId }}</span>
                        <a class="btn btn-xs btn-default btn--inline-flex ml-05"
                           @click="simulate()">
                            Simulate
                        </a>
                    </p>
                </div>
            </template>
        </div>
    </Teleport>
</template>

<script>
import DeviceActionStatus from '@/lib/PaymentMethods/Payyo/Enums/DeviceActionStatus';
import ErrorMessage from '@/vue/components/Errors/ErrorMessage';
import EventNames from '@/lib/Events/EventNames';
import Payment from '@/lib/Payments/Payment';
import PaymentMethodRef from '@/lib/PaymentMethods/PaymentMethodRef';
import PayyoPaymentStatus from '@/lib/PaymentMethods/Payyo/Enums/PayyoPaymentStatus';
import payyoTransactionParser from '@/lib/PaymentMethods/Payyo/payyoTransactionParser';
import PayyoTransactionType from '@/lib/PaymentMethods/Payyo/Enums/PayyoTransactionType';
import PayyoWebSocketStatus from '@/epos/vue/modals/Payment/PaymentMethods/Payyo/PayyoWebSocketStatus';
import SquareBtn from '@/vue/components/SquareBtn';
import StartRestartButtonState from '@/lib/PaymentMethods/StartRestartButtonState';
import TransactionInProgress from '@/lib/Payments/TransactionInProgress';
import UserFacingError from '@/libraries/DigiTickets/Errors/UserFacingError';
import { cloneDeep } from 'lodash';
import { container } from '@/container';
import { fromMinorUnit } from '@/functions/math';
import { getRefundablePayments } from '@/libraries/DigiTickets/Payment/refundablePayments';
import { mapState } from 'vuex';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { suppressModalCancelledErrorOrReThrow } from '@/lib/Errors';
import { timeoutPromise } from '@/functions/functions';
import { updateAngularUi } from '@/functions/angular';

export default {
    name: 'PayyoPaymentMethod',

    components: {
        ErrorMessage,
        PayyoWebSocketStatus,
        SquareBtn,
    },

    data() {
        return {
            /**
             * Has the cancel button been clicked and is a cancel request in progress.
             *
             * @type {boolean}
             */
            cancelling: false,

            /**
             * @type {UserFacingError}
             */
            error: null,

            /**
             * A friendly message that shows the state of the transaction.
             * This is based on currentTransaction.status but is more fine-grained.
             *
             * @type {string}
             */
            friendlyStatusMessage: null,

            /**
             * @type {?PaymentMethod}
             */
            paymentMethod: null,

            /**
             * Is the config valid and has the component loaded?
             *
             * @type {boolean}
             */
            ready: false,

            /**
             * Are we checking if the terminal is online?
             */
            loading: true,

            /**
             * @type {PayyoTransaction}
             */
            currentTransaction: null,

            /**
             * The current start / restart button state. This can also be used to determine the state of the secondary
             * buttons. For example, we can only set a partial payment amount if there isn't a transaction in progress
             * and either button is showing.
             *
             * @type {string}
             */
            currentStartRestartButtonState: StartRestartButtonState.NO_BUTTON,

            autoStartCardTransactions: null,

            showRefundablePaymentsButton: false,

            /**
             * An array of existing payments that are potentially refundable.
             *
             * @type {Payment[]}
             */
            refundablePayments: [],

            /**
             * An array of refund payments that refund or partially refund an existing payment in refundablePayments.
             *
             * @type {Payment[]}
             */
            refundPayments: [],

            /**
             * Should the error continue button be enabled or disabled?
             * When the error first loads it is disabled for a short period of time in order to give things like
             * network or terminal not ready issues time to resolve and make the user actually read and make a note
             * of the error message.
             *
             * @type {boolean}
             */
            enableErrorContinueButton: false,

            /**
             * How long (in ms) after the error is displayed should we wait before enabling the continue button.
             *
             * @type {number}
             */
            enableErrorContinueButtonDelay: 2000,

            /**
             * How long (in ms) after a transaction completes should we wait before enabling the button to start
             * a new transaction.
             *
             * @type {number}
             */
            enableStartButtonAfterTransactionDelay: 1000,

            /**
             * Is a manual re-check of the payment status in progress?
             *
             * @type {boolean}
             */
            recheckingPaymentStatus: false,
        };
    },

    computed: {
        ...mapState(['isDev', 'isStaging']),
        ...mapState('payment', ['requestRefundOfPaymentInProgress']),
    },

    watch: {
        currentTransaction: {
            handler(newValue) {
                if (newValue && newValue.status !== newValue.lastStatus) {
                    console.log('PayyoPaymentStatus: ' + newValue.status);
                    switch (newValue?.status) {
                        case PayyoPaymentStatus.CANCELED:
                            this.currentTransaction.isFinished = true;
                            this.friendlyStatusMessage = 'PAYYO.STATUS__CANCELLED';
                            this.cancelling = false;
                            container.cartService.clearTransactionInProgress();
                            container.paymentState.setPaymentModalCancelable(true, this.paymentMethod.ref);
                            updateAngularUi();
                            break;
                        case PayyoPaymentStatus.CANCEL_ERROR:
                            // We couldn't cancel the payment for some reason.
                            // We know that the payment is not already:
                            // - PayyoPaymentStatus.SUCCEEDED,
                            // - PayyoPaymentStatus.CANCELED,
                            // - PayyoPaymentStatus.FAILED_UNRECOVERABLE
                            // (see PayyoTransaction.addError())
                            // The Cancel button will show automatically so the user can try again or exit the modal.
                            // The transaction has not been captured, but we still need to try and cancel it otherwise
                            // the authorization could remain against the customer's card for several days.
                            this.cancelling = false;
                            this.friendlyStatusMessage = null;
                            this.currentTransaction.status = null;
                            this.currentTransaction.lastStatus = null;
                            container.cartService.clearTransactionInProgress();
                            container.paymentState.setPaymentModalCancelable(true, this.paymentMethod.ref);
                            updateAngularUi();
                            break;
                        case PayyoPaymentStatus.CAPTURE_ERROR:
                            // The transaction has not been captured, but we still need to cancel it otherwise the
                            // authorization could remain against the customer's card for several days.
                            this.friendlyStatusMessage = null;

                            // Wait a short while to display the message then attempt to cancel the payment.
                            timeoutPromise(this.enableErrorContinueButtonDelay).then(() => {
                                this.cancelPayment();
                            });

                            // We could offer a void receipt here, but we don't have any card numbers, so we can't
                            // show last4 etc. is this necessary?
                            break;
                        case PayyoPaymentStatus.FAILED_UNRECOVERABLE:
                            // The transaction has failed and is not recoverable.
                            this.friendlyStatusMessage = 'PAYYO.STATUS__CARD_DECLINED';
                            this.cancelling = false;
                            container.cartService.clearTransactionInProgress();
                            container.paymentState.setPaymentModalCancelable(true, this.paymentMethod.ref);
                            updateAngularUi();
                            break;
                        case PayyoPaymentStatus.FAILED:
                            // The transaction has failed but may still be recoverable. For example a contactless
                            // payment where the customer is asked to insert card and enter pin.
                            container.payyoDriver.getDevice(container.payyoDriver.config.deviceId).then((device) => {
                                if (device?.action?.process_payment_id === this.currentTransaction.paymentId) {
                                    // Just be careful and check the message refers to the current payment.
                                    if (device?.action?.status === DeviceActionStatus.FAILED) {
                                        // We can't try again.
                                        this.currentTransaction.status = PayyoPaymentStatus.FAILED_UNRECOVERABLE;
                                        this.currentTransaction.isFinished = true;
                                        this.currentTransaction.transactionResult = {
                                            message: 'PAYYO.TRANSACTION_MESSAGE__FAILED',
                                            class: 'message-line-danger',
                                        };
                                        return;
                                    }
                                    if (device?.action?.status === DeviceActionStatus.IN_PROGRESS) {
                                        // This payment may still work. For example entering the pin again.
                                        // Updating the status enables the cancel button otherwise we are stuck
                                        // in the payment.
                                        this.currentTransaction.status = PayyoPaymentStatus.REQUIRES_PAYMENT_METHOD;
                                        this.currentTransaction.clearTransactionResult();
                                    }
                                }
                            });
                            break;
                        case PayyoPaymentStatus.PROCESSING:
                            this.friendlyStatusMessage = 'PAYYO.STATUS__PROCESSING';
                            break;
                        case PayyoPaymentStatus.REQUIRES_ACTION:
                            // Payyo do not believe that this is relevant for in person transactions.
                            this.friendlyStatusMessage = 'PAYYO.STATUS__REQUIRES_ACTION';
                            break;
                        case PayyoPaymentStatus.REQUIRES_CAPTURE:
                            this.friendlyStatusMessage = 'PAYYO.STATUS__REQUIRES_CAPTURE';
                            container.payyoDriver.capturePayment(this.currentTransaction).catch((error) => {
                                // Probably some kind of network error.
                                // A successful request that fails to capture will be handled as a CAPTURE_ERROR.
                                this.setError(error);
                            });
                            break;
                        case PayyoPaymentStatus.REQUIRES_PAYMENT_METHOD:
                            if (this.currentTransaction.payment_methods.hasOwnProperty(PayyoTransactionType.CARD_MOTO)) {
                                this.friendlyStatusMessage = 'PAYYO.STATUS__REQUIRES_PAYMENT_METHOD_MOTO';
                                break;
                            }
                            this.friendlyStatusMessage = 'PAYYO.STATUS__REQUIRES_PAYMENT_METHOD';
                            break;
                        case PayyoPaymentStatus.SUCCEEDED:
                            this.friendlyStatusMessage = 'PAYYO.STATUS__SUCCEEDED';
                            this.addPayment();
                            break;
                        default:
                            // An unrecognised status. Just display it.
                            this.userMessage = newValue.status;
                            break;
                    }
                    this.updateStartButtonState();
                    this.updateShowRefundablePaymentsButtonState();
                    this.currentTransaction.lastStatus = this.currentTransaction.status;
                } else {
                    // eslint-disable-next-line no-unsafe-optional-chaining
                    console.log('PayyoPaymentStatus (unchanged): ' + newValue?.status);
                }
            },
            deep: true,
        },
        requestRefundOfPaymentInProgress: {
            handler(newValue) {
                // The user is trying to refund an existing payment that has not been persisted yet.
                // When they clicked the delete button they were already on this payment method.
                this.initiateRefundOfPaymentInProgress(newValue);
            },
        },
    },

    async created() {
        this.paymentMethod = container.paymentMethodService.getByRef(PaymentMethodRef.CARD_PAYYO_CHIP_AND_PIN);

        // Make the import available in the template
        this.StartRestartButtonState = StartRestartButtonState;

        await this.init();
    },

    beforeUnmount() {
        if (this.deregisterCartPaymentChangedFunc) {
            this.deregisterCartPaymentChangedFunc();
        }
        if (this.deregisterPayyoWebSocketConnectedFunc) {
            this.deregisterPayyoWebSocketConnectedFunc();
        }
        if (this.deregisterPayyoWebSocketConnectionErrorFunc) {
            this.deregisterPayyoWebSocketConnectionErrorFunc();
        }
    },

    methods: {
        async init(preventAutoStartCardTransactions = false) {
            try {
                container.payyoDriver.init(true);

                this.ready = true;
                this.autoStartCardTransactions = false;

                const device = await container.payyoDriver.getDevice(container.payyoDriver.config.deviceId);
                if (!preventAutoStartCardTransactions) {
                    this.autoStartCardTransactions = container.currentDevice.device.autoStartCardTransactions;
                }

                this.deregisterCartPaymentChangedFunc = container.$rootScope.$on(
                    EventNames.CART_PAYMENTS_CHANGED,
                    () => {
                        // We need to wait until we receive notification that the refund payment has been added before
                        // enabling the refund button again.
                        this.refundPayments = container.cartService.currentCart.payments;
                        this.updateShowRefundablePaymentsButtonState();
                    }
                );

                this.deregisterPayyoWebSocketConnectedFunc = container.$rootScope.$on(
                    EventNames.PAYYO_WEB_SOCKET_CONNECTED,
                    () => {
                        // The Payyo WebSocket has just connected.
                        // This may be it reconnecting after disconnection, or it may be the first time.
                        // In the case of the former, there is a chance that we missed a message about the
                        // status of the transaction in progress while it was disconnected.
                        // So if there is a transaction in progress, check its status now.
                        if (this.currentTransaction?.paymentId && this.isTransactionInProgress()) {
                            this.recheckPaymentStatus();
                        }
                    }
                );

                this.deregisterPayyoWebSocketConnectionErrorFunc = container.$rootScope.$on(
                    EventNames.PAYYO_WEB_SOCKET_ERROR,
                    () => {
                        this.setError(
                            new UserFacingError(
                                'Unable to connect to Payyo Websocket. Please try again later. '
                                    + 'If there is a transaction in progress please try Manual Reconcilliation'
                            )
                        );
                    }
                );

                if (device.status === 'online') {
                    if (this.requestRefundOfPaymentInProgress) {
                        // The user is trying to refund an existing payment that has not been persisted yet.
                        // When they clicked the delete button they were on a different payment method.
                        this.initiateRefundOfPaymentInProgress(this.requestRefundOfPaymentInProgress);
                        return;
                    }

                    const txnInProgress = container.cartService.getTransactionInProgress();
                    if (txnInProgress && txnInProgress.paymentMethodRef === this.paymentMethod.ref) {
                        console.log('There are transactions to recover');

                        await this.recoverTransaction(txnInProgress);
                        return;
                    }

                    if (container.cartService.getRemainingBalance() < 0) {
                        this.refundablePayments = this.findRefundablePayments();
                    }

                    if (this.autoStartCardTransactions && this.refundablePayments.length === 0) {
                        await this.initiateTransaction();
                    }

                    if (this.refundablePayments.length > 0) {
                        this.refundPayments = container.cartService.currentCart.payments;
                        this.showRefundablePaymentsModal();
                    }

                    // If a transaction was not automatically started this will enable the button to start a transaction.
                    this.updateStartButtonState();
                } else {
                    throw new UserFacingError('The terminal is not ready: ' + device.status);
                }
            } catch (error) {
                this.setError(error);
            } finally {
                this.loading = false;
            }
        },

        async cancelPayment() {
            this.cancelling = true;
            this.friendlyStatusMessage = 'PAYYO.STATUS__CANCELLING';
            this.userMessage = null;
            try {
                await container.payyoDriver.cancelPayment(this.currentTransaction);
            } catch (error) {
                this.cancelling = false;
                await this.setError(error);
            }
        },

        clearError() {
            this.error = null;
        },

        getRemainingBalance() {
            return container.cartService.getRemainingBalance();
        },

        isTransactionInProgress() {
            if (!this.currentTransaction) {
                return false;
            }

            // Finished states are:
            // - PayyoPaymentStatus.CANCELED
            // - PayyoPaymentStatus.FAILED_UNRECOVERABLE
            // - PayyoPaymentStatus.SUCCEEDED
            //
            // currentTransaction.isFinished === true when:
            // currentTransaction.state is one of the above AND we have explicitly set the state because we have
            // completed handling the transaction.

            if (this.currentTransaction.isFinished) {
                return false;
            }
            return true;
        },

        updateShowRefundablePaymentsButtonState() {
            if (!this.ready) {
                this.showRefundablePaymentsButton = false;
                return;
            }

            if (this.isTransactionInProgress()) {
                this.showRefundablePaymentsButton = false;
                return;
            }

            if (
                this.refundablePayments.length > 0
                && container.cartService.getRemainingBalance() < 0
            ) {
                this.showRefundablePaymentsButton = true;
                return;
            }

            this.showRefundablePaymentsButton = false;
        },

        isCancelButtonDisabled() {
            if (this.cancelling) {
                return true;
            }
            const cancelButtonEnabledStates = [
                PayyoPaymentStatus.CANCEL_ERROR,
                PayyoPaymentStatus.REQUIRES_PAYMENT_METHOD,
            ];
            if (cancelButtonEnabledStates.includes(this.currentTransaction?.status)) {
                return false;
            }
            return true;
        },

        isPartialPaymentButtonDisabled() {
            return this.isSecondaryActionButtonDisabled();
        },

        isManualReconciliationButtonDisabled() {
            return this.isSecondaryActionButtonDisabled();
        },

        isMotoPaymentButtonDisabled() {
            return this.isSecondaryActionButtonDisabled();
        },

        isSecondaryActionButtonDisabled() {
            // Disable the button immediately when a transaction starts.
            if (this.isTransactionInProgress()) {
                return true;
            }
            return this.currentStartRestartButtonState === StartRestartButtonState.NO_BUTTON;
        },

        makePartialPayment() {
            container.vueModalFactory
                .open(
                    MODAL_NAMES__EPOS.PARTIAL_PAYMENT,
                    {
                        mode: 'make',
                        isRefund: container.cartService.isRefund(),
                        paymentMethod: container.cartService.getSelectedPaymentMethod(),
                        orderTotal: container.cartService.getTotalExcludingOriginalOrder(),
                        remainingBalance: container.cartService.getRemainingBalance(),
                        maxPayment: container.cartService.getRemainingMaxPayment(),
                    }
                )
                .then((result) => {
                    this.initiateTransaction(result.value);
                })
                .catch(suppressModalCancelledErrorOrReThrow);
        },

        makeMotoPayment() {
            this.initiateTransaction(null, null, PayyoTransactionType.CARD_MOTO);
        },

        showCancelButton() {
            return this.isTransactionInProgress() || this.currentTransaction?.status === PayyoPaymentStatus.CANCEL_ERROR;
        },

        /**
         * Should we show the start button, the restart button, or no button at all?
         *
         * @return {string}
         */
        async updateStartButtonState() {
            if (this.isTransactionInProgress()) {
                this.currentStartRestartButtonState = StartRestartButtonState.NO_BUTTON;
                return;
            }

            if (this.currentTransaction && this.currentTransaction.isFinished) {
                // A transaction has started and has either completed successfully or been cancelled.
                // If it there is an outstanding balance then we need to show the start button.
                if (container.cartService.getRemainingBalance() >= 0) {
                    // Let the user see the success or cancellation message before clearing it.
                    // A tiny delay makes for nicer UX.
                    await timeoutPromise(this.enableStartButtonAfterTransactionDelay);
                    this.currentStartRestartButtonState = StartRestartButtonState.START;
                    return;
                }

                // @TODO We probably need to handle errors and manual reconciliation here.
                // There is no outstanding balance so we don't need to show a button.
                this.currentStartRestartButtonState = StartRestartButtonState.NO_BUTTON;
                return;
            }

            if (!this.autoStartCardTransactions && container.cartService.getRemainingBalance() !== 0) {
                this.currentStartRestartButtonState = StartRestartButtonState.START;
                return;
            }

            this.currentStartRestartButtonState = StartRestartButtonState.NO_BUTTON;
        },

        tryAgain() {
            // Called by restart.
            // Check if there is different logic here or if we even need restart in this integration.
            this.initiateTransaction();
        },

        /**
         * Recover a payment in progress.
         *
         * @param {TransactionInProgress} transactionInProgress
         *
         * @return {Promise<void>}
         */
        async recoverTransaction(transactionInProgress) {
            console.log('Recovering transaction in progress', cloneDeep(transactionInProgress));

            this.currentTransaction = Object.assign(
                container.payyoDriver.createTransaction(transactionInProgress.amount),
                transactionInProgress.state.payyoTransaction
            );
            container.payyoDriver.currentTransaction = this.currentTransaction;
            // Just show a temporary status until we get an update.
            this.friendlyStatusMessage = 'PAYYO.STATUS__IN_PROGRESS';

            // Ensure that the websocket is reconnected.
            await container.payyoDriver.connectWebSocket();

            const response = await container.payyoDriver.getPayment(transactionInProgress.txnRef);

            // Note: we wrap this in data because the driver already returns response.data.
            this.currentTransaction.addTransactionResponse({ data: response });
        },

        /**
         * @param {number} amountInMajorUnits
         * @param {?Payment} existingPayment - for refunds
         * @param {PayyoTransactionType} paymentMethodType
         *
         * @return {Promise<void>}
         */
        async initiateTransaction(amountInMajorUnits, existingPayment, paymentMethodType) {
            this.friendlyStatusMessage = 'PAYYO.STATUS__TRANSACTION_STARTED';
            container.paymentState.setPaymentModalCancelable(false, this.paymentMethod.ref);
            updateAngularUi();

            if (!amountInMajorUnits) {
                amountInMajorUnits = container.cartService.getRemainingBalance();
            }
            // A transaction must be a +ve amount (no refunds)
            if (amountInMajorUnits <= 0 && !existingPayment) {
                return;
            }

            const payyoTransaction = container.payyoDriver.createTransaction(amountInMajorUnits);
            payyoTransaction.setThirdPartyID(container.cartService.generatePaymentThirdPartyID());
            payyoTransaction.setPromptForTips(this.shouldEnableTips());
            payyoTransaction.setDescription(container.userService.currentBranch.name);

            // A standard debit transaction.
            if (!existingPayment) {
                // Is this transaction Card Present or MOTO?
                if (!paymentMethodType) {
                    paymentMethodType = PayyoTransactionType.CARD_PRESENT;
                }
                payyoTransaction.setPaymentMethodsType(paymentMethodType);
            }

            // A refund against an existing transaction via the dedicated refund endpoint.
            // Remember Payyo does not yet support credit / unattached payments.
            // The concept of Card Present vs MOTO does not apply to refunds.
            if (existingPayment) {
                payyoTransaction.paymentId = existingPayment.transactionRef;
                payyoTransaction.originalPayment = existingPayment;
            }

            this.currentTransaction = payyoTransaction;

            try {
                if (existingPayment) {
                    await container.payyoDriver.createRefund(this.currentTransaction);
                } else {
                    await container.payyoDriver.createPayment(this.currentTransaction);
                }

                if (!existingPayment) {
                    // There isn't a GET refund endpoint at the moment, so we can't really recover a refund in
                    // progress at the moment. However, a refund is usually a single request where the response
                    // already indicates success or failure.
                    // Payyo may alternatively end up doing something like including an array of refund objects in
                    // payment responses.
                    const p = cloneDeep(payyoTransaction);
                    // Don't need to keep the last response but reset it rather than just making it null.
                    p.clearLatestResponse();

                    // Remember this transaction is in progress.
                    await container.cartService.setTransactionInProgress(
                        new TransactionInProgress(
                            this.paymentMethod.ref,
                            this.currentTransaction.paymentId,
                            amountInMajorUnits,
                            {
                                payyoTransaction: p,
                            }
                        )
                    );
                }
            } catch (error) {
                await this.setError(error);
            }
        },

        async addPayment() {
            this.clearError();
            try {
                container.cartService.clearTransactionInProgress();
                const payment = new Payment();
                payment.setPaymentMethod(this.paymentMethod);

                if (this.currentTransaction.refundId) {
                    if (!this.currentTransaction.originalPayment.ID) {
                        // This payment we have refunded hasn't been saved yet. DigiTickets has no record of it.
                        // Therefore, just delete the original payment from the cart and do not save the refund.
                        container.cartService.currentCart.removePaymentByTransactionRef(this.currentTransaction.originalPayment.transactionRef);
                        this.$store.commit('payment/setRequestRefundOfPaymentInProgress', null);
                        // Todo It would be nice to offer a receipt here for the refunded payment.
                        updateAngularUi();
                        return;
                    }

                    let refund = this.currentTransaction.latestResponse.refund;

                    try {
                        const originalPayment = await container.payyoDriver.getPayment(this.currentTransaction.paymentId);
                        // Copy over a few bits of information from the payment that we can use on the receipt.
                        refund.last4 = originalPayment?.payment_method?.card_present?.last4;
                        refund.scheme = originalPayment?.payment_method?.card_present?.brand;
                        refund.name = originalPayment?.payment_method?.card_present?.receipt?.application_preferred_name;
                    } catch (e) {
                        console.log(e);
                        // Just ignore the error.
                    }

                    // Todo Get amount from response
                    payment.setTransactionRef(this.currentTransaction.refundId);
                    payment.originalPaymentID = this.currentTransaction.originalPayment.ID;
                    payment.setStatusDetail(JSON.stringify(refund));
                } else {
                    const compactPayment = payyoTransactionParser(this.currentTransaction.latestResponse.payment);
                    payment.setStatusDetail(JSON.stringify(compactPayment));
                    payment.setAuthCode(compactPayment.authCode);
                    payment.setTransactionRef(this.currentTransaction.paymentId);
                    if (compactPayment.tip) {
                        payment.setGratuity(fromMinorUnit(compactPayment.tip));
                    }

                    if (compactPayment.cardTypeRef) {
                        payment.setCardTypeRef(compactPayment.cardTypeRef);
                    }

                    if (compactPayment.last4) {
                        payment.setLastDigits(compactPayment.last4);
                    }
                }

                payment.setTendered(this.currentTransaction.amountInMajorUnits);
                payment.setThirdPartyID(this.currentTransaction.thirdPartyID);

                // Add the payment to the cart.
                // Leave the socket open - it will close automatically after about 5 minutes anyway if idle.
                container.eventBus.broadcast(EventNames.ADD_PAYMENT_TO_CART_REQUESTED, payment);
                updateAngularUi();
            } catch (error) {
                console.error(error);
                // So we won't proceed. Just show an error.
                await this.setError(error);
            } finally {
                // Anything we need to do to tidy up goes here.
                this.currentTransaction.isFinished = true;
                container.paymentState.setPaymentModalCancelable(true, this.paymentMethod.ref);
            }
        },

        /**
         * List all the existing payments that are potentially refundable for this order.
         *
         * @returns {Payment[]}
         */
        findRefundablePayments() {
            if (container.cartService.currentCart.originalOrder) {
                return getRefundablePayments(
                    container.cartService.currentCart.originalOrder.payments,
                    [this.paymentMethod.ref]
                );
            }
            return [];
        },

        showRefundablePaymentsModal() {
            this.showRefundablePaymentsButton = false;
            container.vueModalFactory
                .open(MODAL_NAMES__EPOS.REFUNDABLE_PAYMENTS, {
                    payments: this.refundablePayments,
                    refundPayments: this.refundPayments,
                    refundNeeded: container.cartService.getRemainingBalance(),
                })
                .then((refundData) => {
                    this.refundPayment(refundData);
                })
                .catch((error) => {
                    this.showRefundablePaymentsButton = true;
                    suppressModalCancelledErrorOrReThrow(error);
                });
        },

        refundPayment(refundData) {
            this.initiateTransaction(
                refundData.refundAmount,
                refundData.payment,
            );
        },

        initiateRefundOfPaymentInProgress(existingTransactionRef) {
            if (existingTransactionRef) {
                const existingPayment = container.cartService.currentCart.payments.find((payment) => existingTransactionRef === payment.transactionRef);
                this.refundPayment(
                    {
                        refundAmount: existingPayment.amount * -1,
                        payment: existingPayment,
                    }
                );

                return;
            }
            container.eventBus.broadcast(EventNames.SET_VOID_OR_REFUND_IN_PROGRESS, null);
        },

        reset() {
            this.loading = true;
            this.error = null;
            this.enableErrorContinueButton = false;
            this.friendlyStatusMessage = null;
        },

        postErrorHandler() {
            console.log('Continue button clicked after error');
            this.reset();

            if (this.currentTransaction) {
                if (!this.currentTransaction.paymentId) {
                    this.currentTransaction = null;
                } else {
                    // We should check the state of the payment.
                }
            }

            // We don't want transactions to start automatically because otherwise the user has no opportunity
            // to break out of an error cycle.
            this.init(true);
        },

        shouldEnableTips() {
            return container.cartService.currentCart.containsKitchenItems()
                && container.userService.company.gratuityEnabled;
        },

        async startManualReconciliation() {
            container.vueModalFactory
                .open(MODAL_NAMES__EPOS.PAYYO_MANUAL_RECONCILIATION, {
                    deviceId: container.payyoDriver.config.deviceId,
                    filterPayments: false,
                    outstandingBalance: container.cartService.getRemainingBalance(),
                })
                .then((payment) => {
                    if (payment) {
                        // Note: the user has already confirmed after selecting the payment.

                        // Note: Depending on how often manual reconciliations are used we may want to consider
                        // handling payments that were authorised but not captured too because these payments will
                        // stay appear as authorised on the customers card for a while. We could either cancel or
                        // capture these.

                        // We want to make these payments identifiable, so add a flag.
                        payment.isManualReconciliation = true;
                        // Reset the paymentId otherwise it will reject.
                        this.currentTransaction.paymentId = payment.id;
                        this.currentTransaction.addTransactionResponse({ data: payment });
                    }
                })
                .catch(suppressModalCancelledErrorOrReThrow);
        },

        async setError(error) {
            this.error = error;

            // This allows the user to exit the modal if there is a persistent error.
            container.paymentState.setPaymentModalCancelable(true, this.paymentMethod.ref);
            updateAngularUi();

            // Enable the continue/retry button in the error message after 3 seconds.
            await timeoutPromise(this.enableErrorContinueButtonDelay);
            this.enableErrorContinueButton = true;
        },

        simulate() {
            container.vueModalFactory.openAndIgnoreResult(
                MODAL_NAMES__EPOS.SIMULATE_PAYYO_PAYMENT,
                {
                    transaction: this.currentTransaction,
                }
            );
        },

        async recheckPaymentStatus() {
            if (!this.currentTransaction?.paymentId) {
                throw new Error('Cannot recheck payment status because there is no current paymentId.');
            }

            this.recheckingPaymentStatus = true;

            try {
                const response = await container.payyoDriver.getPayment(this.currentTransaction.paymentId);
                console.debug('Loaded fresh payment status', response);
                await container.payyoDriver.currentTransaction.addTransactionResponse({ data: response });
            } catch (err) {
                console.error('Error rechecking payment status', err);
                // Couldn't get a fresh status. Not much we can do about that...
            } finally {
                this.recheckingPaymentStatus = false;
            }
        },
    },
};
</script>
